<template>
<div class="settings">
    <div class="columns">
        <div class="column is-one-quarter">
            <div class="columns">
                <div class="column is-11">
                    <settings-menu active-item="servers"></settings-menu>
                </div>
            </div>
        </div>
        <div class="column">
            <div class="settings-container">
                <div class="columns">
                    <div v-if="loadingServer" class="column is-full has-text-centered">
                        <img src="/loader-dark.svg">
                    </div>
                    <div v-if="!loadingServer" class="column is-full">
                        <div v-if="server" class="columns is-multiline">
                            <div class="column is-10">
                                <div class="plan-page-label">
                                    <font-awesome-icon icon="server"/><span style="display: inline-block; margin-top: -4px;">&nbsp;&nbsp;{{ 'Server: ' + server.name }}</span>
                                </div>
                                <div class="plan-page-description" style="font-size:13px !important; margin-top: 0px;">{{ server.host }}</div>
                            </div>
                            <div class="column is-2 has-text-right">
                                <div class="plan-page-label">
                                    <div class="dropdown is-right is-hoverable dropdown-trigger cursor-pointer">
                                        <span style="display: inline-block; margin-top: -4px;">Server settings&nbsp;&nbsp;</span><font-awesome-icon icon="cog"/>
                                        <div class="dropdown-menu" role="menu">
                                            <div class="dropdown-content">
                                                <a @click="showChangeServerDefaultDatabaseStorageModal" class="dropdown-item">
                                                    <span><font-awesome-icon icon="hdd" class="dropdown-action-icon"/>&nbsp;&nbsp;Change default Storage</span>
                                                </a>
                                                <a @click="showChangeServerDefaultDatabaseBackupSettingsModal" class="dropdown-item">
                                                    <span><font-awesome-icon icon="tools" class="dropdown-action-icon"/>&nbsp;&nbsp;Change default Backup Settings</span>
                                                </a>
                                                <a @click="showConfigureDatabsesAutoSyncrhonizationModal" class="dropdown-item">
                                                    <span><font-awesome-icon icon="sync-alt" class="dropdown-action-icon"/>&nbsp;&nbsp;Databases auto-synchorinization</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="column is-12">
                                <div class="columns is-multiline">
                                    <div class="column is-6">
                                        <div class="field">
                                            <input v-model="name" type="text" @keyup.enter="updateServer" :class="{ 'is-danger': hasError('name') }" class="input custom-input-size is-medium" placeholder="Server name" autocomplete="off" :disabled="nameIsDisabled"/>
                                            <span v-if="!hasError('name')" class="help">A friendly name to easily recognize the server later.</span>
                                            <span v-if="hasError('name')" class="help is-danger">{{ getError('name') }}</span>
                                        </div>
                                    </div>
                                    <div class="column is-6">
                                        <div class="field">
                                            <input v-model="user" type="text" @keyup.enter="updateServer" :class="{ 'is-danger': hasError('user') }" class="input custom-input-size is-medium" placeholder="User" autocomplete="off" :disabled="userIsDisabled"/>
                                            <span v-if="!hasError('user')" class="help">This user will be used to take the backups.</span>
                                            <span v-if="hasError('user')" class="help is-danger">{{ getError('user') }}</span>
                                        </div>
                                    </div>
                                    <div class="column is-6">
                                        <div class="field">
                                            <input type="text" v-model="host" @keyup.enter="updateServer" :class="{ 'is-danger': hasError('host') }" class="input custom-input-size is-medium" placeholder="Host" autocomplete="off" :disabled="hostIsDisabled"/>
                                            <span v-if="!hasError('host')" class="help">The IP or hostname of the server.</span>
                                            <span v-if="hasError('host')" class="help is-danger">{{ getError('host') }}</span>
                                        </div>
                                    </div>
                                    <div class="column is-6">
                                        <div class="field">
                                            <input type="text" v-model="port" @keyup.enter="updateServer" :class="{ 'is-danger': hasError('port') }" class="input custom-input-size is-medium" placeholder="Port" autocomplete="off" :disabled="portIsDisabled"/>
                                            <span v-if="!hasError('port')" class="help">Server port. Usually, the default one is 22.</span>
                                            <span v-if="hasError('port')" class="help is-danger">{{ getError('port') }}</span>
                                        </div>
                                    </div>
                                    
                                    <div class="column is-full">
                                        <div class="columns is-multiline" style="margin-top: 10px;">
                                            <div class="column is-3 tooltip is-tooltip-multiline" data-tooltip="Coming soon. Contact the support if you need to change the details of the server.">
                                                <div @click="updateServer" :class="{ 'is-loading': this.updatingServer }" class="button custom-button-size is-fullwidth is-info" :disabled="saveButtonIsDisabled">Save Changes</div>
                                            </div>
                                            <div class="column is-3 has-text-centered">
                                                <div :class="{ 'tooltip is-tooltip-multiline': notDeleteableReason }" :data-tooltip="notDeleteableReason">
                                                    <div @click="deleteServer" class="close-add-monitor-modal button custom-button-size is-fullwidth is-danger" :disabled="!canBeDeleted">Delete Server</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <change-server-default-database-storage v-if="changeServerDefaultDatabaseStorageModalIsVisible" :server="server"></change-server-default-database-storage>
    <change-server-default-database-backup-settings v-if="changeServerDefaultDatabaseBackupSettingsModalIsVisible" :server="server"></change-server-default-database-backup-settings>
    <configure-databases-auto-synchronization v-if="configureDatabasesAutoSynchronizationModalIsVisible" :server="server"></configure-databases-auto-synchronization>    
</div>
</template>

<script>
import Api from '@/api';
import Swal from 'sweetalert2';
import FormErrorsMixin from '@/mixins/FormErrors';

import SettingsMenu from '@/components/Settings/Menu.vue';
import ChangeServerDefaultDatabaseStorage from '@/components/Servers/ChangeServerDefaultDatabaseStorage.vue';
import ChangeServerDefaultDatabaseBackupSettings from '@/components/Servers/ChangeServerDefaultDatabaseBackupSettings.vue';
import ConfigureDatabasesAutoSynchronization from '@/components/Servers/ConfigureDatabasesAutoSynchronization.vue';

export default {
    components: {
        SettingsMenu,
        ChangeServerDefaultDatabaseStorage,
        ChangeServerDefaultDatabaseBackupSettings,
        ConfigureDatabasesAutoSynchronization,
    },

    mixins: [FormErrorsMixin],

    metaInfo: {
        title: 'Server Settings - Weap.io',
    },

    data() {
        return {
            server: null,
            name: '',
            host: '',
            user: '',
            port: '',
            changeServerDefaultDatabaseStorageModalIsVisible: false,
            changeServerDefaultDatabaseBackupSettingsModalIsVisible: false,
            configureDatabasesAutoSynchronizationModalIsVisible: false,

            loadingServer: false,
            updatingServer: false,
            allowedInputs: ['name', 'access_key', 'secret_key'],
        }
    },

    mounted() {
        this.getServer();
        this.listenEvents();
    },

    methods: {
        getServer(successCallback) {
            if (this.loadingServer) {
                return;
            }

            this.loadingServer = true;

            Api().get('servers/' + this.$route.params.id)
                .then((response) => {
                    this.server = response.data.data;
                    if (typeof successCallback == 'function') {
                        successCallback();
                    }
                })
                .catch((error) => {
                    //
                })
                .finally(() => {
                    this.loadingServer = false;
                });
        },

        updateServer() {
            //
        },

        deleteServer() {
            return;
            swal({
                title: 'Are you sure?',
                text: 'The storage connection will be permanently removed.',
                dangerMode: true,
                buttons: {
                    cancel: 'Cancel',
                    confirm: {
                        text: 'Yes, I\'m sure!',
                        value: true,
                        closeModal: false,
                    }
                },
                icon: 'warning',
            })
            .then((value) => {
                if (!value) {
                    swal.close();
                    return;
                }

                return Api().delete('storages/' + this.storage.id)
                    .then((success) => {
                        this.$router.push({ name: 'storagesSettings', query: { storage_deleted: true} });
                    });
            });
        },

        makeStorageDefault() {
            if (this.storage.is_default) {
                return;
            }

            swal({
                title: 'Are you sure?',
                text: 'From now on, this storage will be used as default for the new servers. Existent servers will not be affected by this change.',
                icon: 'info',
                buttons: {
                    cancel: 'Cancel',
                    confirm: {
                        text: 'Yes, make it default',
                        value: true,
                        closeModal: false,
                    }
                },
            })
            .then((value) => {
                if (!value) {
                    swal.close();
                    return;
                }

                return Api().put('storages/' + this.storage.id + '/is-default')
                    .then((success) => {
                        this.getStorage(() => {
                            swal('Success!', 'The default storage was changed.', 'success');
                        });
                    });
            });
        },

        showChangeServerDefaultDatabaseStorageModal() {
            this.changeServerDefaultDatabaseStorageModalIsVisible = true;
        },

        showChangeServerDefaultDatabaseBackupSettingsModal() {
            this.changeServerDefaultDatabaseBackupSettingsModalIsVisible = true;
        },

        showConfigureDatabsesAutoSyncrhonizationModal() {
            this.configureDatabasesAutoSynchronizationModalIsVisible = true;
        },

        listenEvents() {
            Event.$on('refreshServer', (successCallback) => {
                this.getServer(successCallback);
            });

            Event.$on('changeServerDefaultDatabaseStorageModalClosed', () => {
                this.changeServerDefaultDatabaseStorageModalIsVisible = false;
            });

            Event.$on('serverDefaultDatabaseStorageChanged', (callback) => {
                this.changeServerDefaultDatabaseStorageModalIsVisible = false;
                this.getServer(callback);
            });

            Event.$on('changeServerDefaultDatabaseBackupSettingsModalClosed', () => {
                this.changeServerDefaultDatabaseBackupSettingsModalIsVisible = false;
            });

            Event.$on('configureDatabasesAutoSynchronizationModalClosed', () => {
                this.configureDatabasesAutoSynchronizationModalIsVisible = false;
            });

            Event.$on('serverDefaultDatabaseBackupSettingsChanged', (callback) => {
                this.getServer(callback);
                this.changeServerDefaultDatabaseBackupSettingsModalIsVisible = false;
            });
        },
    },

    computed: {
        canBeDeleted() {
            return false;
        },

        notDeleteableReason() {
            return 'Coming soon. Contact the support in order to delete the server.';
        },

        nameIsDisabled() {
            return true;

            if (this.updatingServer) {
                return true;
            }

            return false;
        },

        hostIsDisabled() {
            return true;

            if (this.updatingServer) {
                return true;
            }

            return false;
        },

        userIsDisabled() {
            return true;
            
            if (this.updatingServer) {
                return true;
            }

            return false;
        },

        portIsDisabled() {
            return true;

            if (this.updatingServer) {
                return true;
            }

            return false;
        },

        saveButtonIsDisabled() {
            return true;

            if (this.updatingServer) {
                return true;
            }

            if (this.loadingServer) {
                return true;
            }

            return false;
        },

        serverData() {
            return {
                name: this.name,
                user: this.user,
                host: this.host,
                port: this.port,
            };
        },
    },

    watch: {
        server(server) {
            if (!server) {
                return;
            }

            this.name = server.name;
            this.user = server.user;
            this.host = server.host;
            this.port = server.port;
        },
    },  
}
</script>
